import { useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import type { Dayjs } from "dayjs";
import noop from "lodash/noop";
import styled from "styled-components";

import { customRangeFormat, fullMonthAndYear } from "@similarweb/pro-logic-components";
import { SidebarListCompactElementItem } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { CountryFilter } from "app/components/filters-bar/country-filter/CountryFilter";
import { CountryFilterCompact } from "app/components/filters-bar/country-filter/CountryFilterCompact";
import * as utils from "app/components/filters-bar/utils";
import { WebSourceFilter } from "app/components/filters-bar/websource-filter/WebSourceFilter";
import ResponsiveFiltersBarPro from "app/components/React/ResponsiveFiltersBarPro/ResponsiveFiltersBarPro";
import { DurationSelectorSidebarButton } from "app/pages/website-analysis/DurationSelectorSidebarButton";
import { FixedDurationsSelector } from "app/components/duration-selector/FixedDurationsSelector";
import type { ICountryObject } from "app/services/CountryService";

const MonitorKeywordsFiltersContainer = styled.span`
    .SidebarList-panel-control--open,
    .SidebarList-panel-view--open,
    .SidebarListCompactElement {
        overflow: auto;
    }
`;

const getDisabledDurationSelectorPreset = () => {
    const from = (swSettings.current?.endDate as Dayjs)?.clone();
    const displayText = from?.format(fullMonthAndYear);
    const initialPreset = from?.format(`${customRangeFormat}-${customRangeFormat}`);

    const durationSelectorPreset = [
        {
            id: initialPreset,
            text: displayText,
            disabled: false,
            displayText,
            locked: false,
            value: initialPreset,
        },
    ];
    return { initialPreset, durationSelectorPreset };
};

interface IMonitorKeywordsResponsiveFiltersProps {
    selectedCountryId: number;
    selectedCountryText: string;
    availableCountries: ICountryObject[];
    onCountryChange: (country: number | ICountryObject) => void;
}

export const MonitorKeywordsResponsiveFilters = ({
    selectedCountryId,
    selectedCountryText,
    onCountryChange,
    availableCountries,
}: IMonitorKeywordsResponsiveFiltersProps) => {
    const { state, params } = useCurrentStateAndParams();
    const [isSidebarOpen, setSidebarOpen] = useState<boolean>(false);
    const { webSource } = params;
    const { initialPreset, durationSelectorPreset } = getDisabledDurationSelectorPreset();

    const availableWebSources = utils.getAvailableWebSource(state, params);
    const currentWebSource = availableWebSources.find((ws) => ws.id === webSource);

    const toggleSidebar = () => {
        setSidebarOpen(!isSidebarOpen);
    };

    const onSideBarToggle = (isOpen) => {
        setSidebarOpen(isOpen);
    };

    const onFiltersSidebarListCompactElementItemChangeProp = (item, cb) => {
        return async () => {
            setSidebarOpen(false);
            setTimeout(() => {
                cb(item);
            }, 400);
        };
    };

    return (
        <MonitorKeywordsFiltersContainer>
            <ResponsiveFiltersBarPro
                isSideBarOpen={isSidebarOpen}
                toggleSidebar={toggleSidebar}
                onSidebarToggle={onSideBarToggle}
                fixedCompactFilters={[]}
                filters={[
                    {
                        id: "duration",
                        filter: (
                            <FixedDurationsSelector
                                key="filter-duration"
                                isDisabled
                                onSubmit={noop}
                                compareAllowed={false}
                                initialComparedDuration={false}
                                componentName={swSettings.current.componentId}
                                keys={""}
                                appendTo="body"
                                placement="bottom-right"
                                initialPreset={initialPreset}
                                presets={durationSelectorPreset}
                            />
                        ),
                        compactFilter: {
                            listItem: (
                                <DurationSelectorSidebarButton
                                    key="filter-duration"
                                    presets={durationSelectorPreset}
                                    initialPreset={initialPreset}
                                    compareSelected={false}
                                    disabled
                                />
                            ),
                            compactElement: <></>,
                        },
                    },
                    {
                        id: "country",
                        filter: (
                            <CountryFilter
                                key="country-filter"
                                height={64}
                                availableCountries={availableCountries}
                                changeCountry={onCountryChange}
                                selectedCountryIds={{ [selectedCountryId]: true }}
                                appendTo={"body"}
                                dropdownPopupPlacement="bottom-right"
                            />
                        ),
                        compactFilter: {
                            listItem: (
                                <SidebarListCompactElementItem
                                    key="filter-country"
                                    iconClass={`country-icon country-icon-${selectedCountryId}`}
                                    title={selectedCountryText}
                                />
                            ),
                            compactElement: (
                                <CountryFilterCompact
                                    countries={availableCountries}
                                    onChange={onFiltersSidebarListCompactElementItemChangeProp}
                                    selectedCountry={{ [selectedCountryId]: true }}
                                    onChangeCallBack={onCountryChange}
                                />
                            ),
                        },
                    },
                    {
                        id: "websource",
                        filter: (
                            <WebSourceFilter
                                key="websource-filter"
                                items={availableWebSources}
                                onChange={noop}
                                selectedIds={{ [currentWebSource.id]: true }}
                                height={64}
                                appendTo={"body"}
                                dropdownPopupPlacement="bottom-right"
                                disabled
                            />
                        ),
                        compactFilter: {
                            listItem: (
                                <SidebarListCompactElementItem
                                    key="filter-websource"
                                    iconClass={`icon icon-${currentWebSource.icon}`}
                                    title={currentWebSource.text}
                                    disabled
                                />
                            ),
                            compactElement: <></>,
                        },
                    },
                ]}
            />
        </MonitorKeywordsFiltersContainer>
    );
};
